import { defaultColumnWidths } from "@/lib/interfaces and Enums/ENUMColumnWidths";
import { ExecutionRequestStatus } from "@/lib/interfaces and Enums/ENUMExecutionRequestStatus";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { InstrumentTypeCode } from "@/lib/interfaces and Enums/ENUMInstrumentTypeCode";
import { SecurityTypeCode } from "@/lib/interfaces and Enums/ENUMSecurityTypeCode";
import { StructureTypeCode } from "@/lib/interfaces and Enums/ENUMStructureTypeCode";
import { TableHeaderField } from "@/lib/interfaces and Enums/ENUMTableHeaders";
import { TradeType } from "@/lib/interfaces and Enums/ENUMTradeType";
import { fillerColDef } from "@/lib/fillerColDef";
import {
  returnFormattedDate,
  returnSizeFormatted,
} from "@/lib/globalFormatterHelper";
import tradeDataStore from "@/store/tradeDataStore";
import { ColDef } from "ag-grid-community";
import {
  isCPIEditable,
  reorderColumnsForOrders,
  returnColDefsAsNewArray,
  tradeHasBondOrBondOption,
  wrapColDefsForOrderFills,
  getStartFillerWidth,
} from "../actionSummaryColDefHelperFunctions";
import { applyColumnWidthMultiplier } from "../actionSummaryHelper";
import { IColDefsObject } from "../actionSummaryMainTableColDefs";
import CashCellEditor from "../cellEditors/CashCellEditor";
import CPICellEditor from "../cellEditors/CPICellEditor";
import DynamicCellEditorQuoteDollarFraction from "../cellEditors/DynamicCellEditorQuoteDollarFraction";
import DynamicCellEditorQuoteSize from "../cellEditors/DynamicCellEditorQuoteSize";
import AddFillButtonCellRenderer from "../cellRenderers/AddFillButtonCellRenderer";
import BookCellRenderer from "../cellRenderers/BookCellRenderer";
import CashCellRenderer from "../cellRenderers/CashCellRenderer";
import CouponActionSummarySwapCellRenderer from "../cellRenderers/CouponActionSummarySwapCellRenderer";
import { couponCellValueFormatter } from "../cellRenderers/CouponActionSummarySwapCellRenderer/couponActionSummarySwapFormatter";
import CPICellRenderer from "../cellRenderers/CPICellRenderer";
import CpiIndexCellRenderer from "../cellRenderers/CpiIndexCellRenderer";
import DealerCellRenderer from "../cellRenderers/DealerCellRenderer";
import { dealerCellValueFormatter } from "../cellRenderers/dealerCellFormatter";
import { cashCellValueFormatter } from "../cellRenderers/cashCellFormatter";
import { dealtFeeCellValueFormatter } from "../cellRenderers/dealtFeeCellFormatter";
import DealtFeeCellRenderer from "../cellRenderers/DealtFeeCellRenderer";
import DealtPriceCellRenderer from "../cellRenderers/DealtPriceCellRenderer";
import { dealtPriceValueFormatter } from "../cellRenderers/DealtPriceCellRenderer/dealtPriceFormatter";
import {
  CurrentCouponCellRenderer,
  QuotedMarginCellRenderer,
  ReferenceIndexCellRenderer,
} from "../cellRenderers/FRNBlockCellRenderers";
import {
  currentCouponValueFormatter,
  quotedMarginValueFormatter,
  referenceIndexValueFormatter,
} from "../cellRenderers/FRNBlockCellRenderers/frnBlockFormatter";
import DirectionCellRenderer from "../cellRenderers/DirectionCellRenderer/index";
import { directionValueFormatter } from "../cellRenderers/DirectionCellRenderer/directionFormatter";
import EditFillButtonCellRenderer from "../cellRenderers/EditFillButtonCellRenderer";
import ExecutionFeeCellRenderer from "../cellRenderers/ExecutionFeeCellRenderer";
import ExpandCollapseAllHeader from "../cellRenderers/ExpandCollapseAllHeader";
import ExpiryDateCellRenderer from "../cellRenderers/ExpiryDateCellRenderer";
import FillExpandCellRenderer from "../cellRenderers/FillExpandCellRenderer";
import FilledAtCellRenderer from "../cellRenderers/FilledAtCellRenderer";
import { filledAtValueFormatter } from "../cellRenderers/FilledAtCellRenderer/filledAtFormatter";
import FilledPercentCellRenderer from "../cellRenderers/FilledPercentCellRenderer";
import { filledPercentValueFormatter } from "../cellRenderers/FilledPercentCellRenderer/filledPercentFormatter";
import FillPriceCellEditor from "../cellRenderers/FillPriceCellRenderer/FillPriceCellEditor";
import FillPriceCellRenderer from "../cellRenderers/FillPriceCellRenderer";
import { fillPriceValueFormatter } from "../cellRenderers/FillPriceCellRenderer/fillPriceFormatter";
import { isFillPriceCellEditable } from "../cellRenderers/FillPriceCellRenderer/fillPriceHelpers";
import FillQuantityCellEditor from "../cellRenderers/FillQuantityCellRenderer/FillQuantityCellEditor";
import FillQuantityCellRenderer from "../cellRenderers/FillQuantityCellRenderer";
import { fillQuantityValueFormatter } from "../cellRenderers/FillQuantityCellRenderer/fillQuantityFormatter";
import { isFillQuantityCellEditable } from "../cellRenderers/FillQuantityCellRenderer/fillQuantityHelpers";
import {
  FillsSummaryPercentCellRenderer,
  fillsSummaryPercentValueFormatter,
  FillsSummaryPriceCellRenderer,
  fillsSummaryPriceValueFormatter,
  FillsSummaryQuantityCellRenderer,
  fillsSummaryQuantityValueFormatter,
} from "../cellRenderers/FillsSummary";
import IndicativeMidCellRenderer from "../cellRenderers/IndicativeMidCellRenderer";
import LevelCellRenderer from "../cellRenderers/LevelCellRenderer";
import { levelValueFormatter } from "../cellRenderers/LevelCellRenderer/levelFormatter";
import NotionalLeg1 from "../cellRenderers/NotionalLeg1";
import { notionalLeg1ValueFormatter } from "../cellRenderers/NotionalLeg1/notionalLeg1Formatter";
import QuotedPercentCellRenderer from "../cellRenderers/QuotedPercentCellRenderer";
import { quotedPercentValueFormatter } from "../cellRenderers/QuotedPercentCellRenderer/quotedPercentFormatter";
import quotedSizeLegRP from "../cellRenderers/quotedSize/QuotedSizeLegRP";
import QuoteCellRenderer from "../cellRenderers/QuoteCellRenderer";
import RemoveFillButtonCellRenderer from "../cellRenderers/RemoveFillButtonCellRenderer";
import SettlementDateCellRenderer from "../cellRenderers/SettlementDateCellRenderer";
import { settlementDateValueFormatter } from "../cellRenderers/SettlementDateCellRenderer/settlementDateFormatter";
import StrikeCellRenderer from "../cellRenderers/StrikeCellRenderer";
import UnwindTypeCellRenderer from "../cellRenderers/UnwindTypeCellRenderer";
import { returnTrueIfCellCanBeEdited } from "../columnHelpers/cellCanBeEdited";
import { ColId } from "../ENUMActionSummaryColIds";
import {
  displayPendingTooltipForFilledPercent,
  displayPendingTooltipForFillQuantity,
  displayYieldTooltipForAllQuoteLevels,
  displayYieldTooltipForFills,
} from "../tooltips/tooltipHelperFunctions";
import {
  quoteValueFormatter,
  statusAtAffirmedRange,
  statusAtExecutedRange,
  statusFormExecutedToAffirmedRange,
  statusFromAwarded,
  statusFromReceivedToQuoted,
  statusToShowDealtPrice,
  // LQC-275: % Quoted column disabled on RP leg until RP-side behaviour is finalised
  // tradeHasReducedQuotedSize,
} from "./colDefsShared";
import CashDealerTooltip from "../tooltips/CashDealerTooltip";
import { feeAdjustedYieldTooltipValueGetter } from "../tooltips/FeeAdjustedYieldTooltip";

export function returnLegColDefsRP({
  theme,
  fullTradeData,
  columnWidthMultiplier = 1.0,
}: IColDefsObject) {
  const isOrder = fullTradeData?.type === TradeType.Order;

  const legColDefsRP = [
    {
      field: "",
      headerName: " ",
      width: defaultColumnWidths.expandArrow,
      fixedWidth: true,
      cellRenderer: isOrder ? FillExpandCellRenderer : "agGroupCellRenderer",
      resizable: false,
      suppressMovable: true,
      pinned: "left",
      colId: ColId.expand,
      lockPosition: "left",
      cellClass: "detailExpandCellRendererArrow",
      headerClass: "fixedWidthHeader",
      mainMenuItems: ["resetColumns"],
      cellRendererParams: { fullTradeData: fullTradeData },
      headerComponent: ExpandCollapseAllHeader,
    },
    {
      field: "startFiller",
      colId: ColId.startFiller,
      headerName: " ",
      width: getStartFillerWidth(columnWidthMultiplier), // 0.9→50, 1→53, 1.2→59
      fixedWidth: true,
      resizable: false,
      suppressMovable: true,
      hide: true,
      lockPosition: "left",
      suppressColumnsToolPanel: true,
      headerClass: "fixedWidthHeader",
      cellClass: "fixedWidthCell",
    },
    {
      field: "bid",
      tooltipValueGetter: displayYieldTooltipForAllQuoteLevels,
      headerName: TableHeaderField.bid,
      colId: ColId.bid,
      editable: (params: any) =>
        returnTrueIfCellCanBeEdited(params.data.statusER, ColId.bid),
      width: defaultColumnWidths.quoteField,
      cellClass: `ag-roundedInputStyle`,
      cellEditor: DynamicCellEditorQuoteDollarFraction, // Use the custom editor
      cellRendererParams: { fullTradeData: fullTradeData, tableLevel: "Leg" },
      cellRenderer: QuoteCellRenderer,
      valueFormatter: quoteValueFormatter,
    },
    {
      field: "ask",
      tooltipValueGetter: displayYieldTooltipForAllQuoteLevels,
      headerName: TableHeaderField.ask,
      colId: ColId.ask,
      editable: (params: any) =>
        returnTrueIfCellCanBeEdited(params.data.statusER, ColId.ask),
      cellDataType: "text",
      cellEditor: DynamicCellEditorQuoteDollarFraction, // Use the custom editor
      cellClass: "ag-roundedInputStyle",
      width: defaultColumnWidths.quoteField,
      cellRendererParams: { fullTradeData: fullTradeData, tableLevel: "Leg" },
      cellRenderer: QuoteCellRenderer,
      valueFormatter: quoteValueFormatter,
    },
    {
      field: "mid",
      tooltipValueGetter: displayYieldTooltipForAllQuoteLevels,
      headerName: TableHeaderField.mid,
      colId: ColId.mid,
      cellDataType: "text",
      cellEditor: DynamicCellEditorQuoteDollarFraction, // Use the custom editor
      cellClass: "ag-roundedInputStyle",
      width: defaultColumnWidths.quoteField,
      cellRendererParams: { fullTradeData: fullTradeData, tableLevel: "Leg" },
      cellRenderer: QuoteCellRenderer,
      valueFormatter: quoteValueFormatter,
    },
    {
      field: "feeAdjustedQuote",
      headerName: TableHeaderField.feeAdjustedQuote, // LEG LEVEL cannot use the header component as it does not know it's context in the table.
      colId: ColId.feeAdjustedQuote,
      width: defaultColumnWidths.feeAdjustedQuote,
      cellRenderer: DealtPriceCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
      valueFormatter: dealtPriceValueFormatter,
      tooltipValueGetter: feeAdjustedYieldTooltipValueGetter
      
    },
    {
      field: "unwind",
      headerName: TableHeaderField.unwind,
      extraLargeScaling: true,
      colId: ColId.unwind,
      width: defaultColumnWidths.unwind,
      cellRenderer: UnwindTypeCellRenderer,
      valueFormatter: (params: any) => params.value,
      cellRendererParams: { fullTradeData: fullTradeData },
    },
    {
      field: "dealer",
      headerName: TableHeaderField.dealer,
      colId: ColId.dealer,
      width: defaultColumnWidths.dealer,
      headerClass: "noRightBorder",
      cellRenderer: DealerCellRenderer,
      valueFormatter: dealerCellValueFormatter,
      cellRendererParams: { fullTradeData: fullTradeData, theme: theme },
      tooltipValueGetter: (params: any) => params.data ? "dealer" : null,
      tooltipComponent: CashDealerTooltip,
    },
    {
      field: "cash",
      colId: ColId.cash,
      headerName: TableHeaderField.cash,
      width: defaultColumnWidths.cash,
      editable: (params: any) => {
        const currentTradeData = tradeDataStore.getState().tradeData;
        const thisTrade = currentTradeData.find(
          (trade) => trade.esid == fullTradeData.esid,
        );
        const thisBlockId = params.data.blockId;

        if (thisTrade && thisTrade.status) {
          if (statusAtExecutedRange.includes(thisTrade.status)) {
            return false;
          }
          // If the trade is UNWIND = True
          if (
            thisTrade.unwind == true &&
            statusFromAwarded.includes(thisTrade.status)
          ) {
            return true;
          }
          // Get block for NON-NPV style
          const thisBlock = thisTrade.legs?.find(
            (leg) => leg.blockId == thisBlockId,
          );
          if (thisBlock) {
            if (
              (thisBlock.unwind == "Recoupon" &&
                statusFromAwarded.includes(thisTrade.status)) || //statusFormExecutedToAffirmedRange // #14876, #14877, #14878, #14879, #14880, #14881 - Moving ReCoupon showing on Dealer and Cash cells forward to Awarded.
              thisBlock.unwind == "NPV"
            ) {
              if (thisTrade.status != ExecutionRequestStatus.DealtAway) {
                return true;
              }
            }
          }
        }
        return false;
      },
      cellEditor: CashCellEditor,
      cellRenderer: CashCellRenderer,
      valueFormatter: cashCellValueFormatter,
      cellRendererParams: { fullTradeData: fullTradeData },
    },
    {
      field: "dealtFee", //npv Cash(fee inc),
      headerName: TableHeaderField.dealtFee,
      colId: ColId.dealtFee,
      width: defaultColumnWidths.dealtFee,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellRenderer: DealtFeeCellRenderer,
      valueFormatter: dealtFeeCellValueFormatter,
      tooltipValueGetter: (params: any) => params.data ? "dealtFee" : null,
      tooltipComponent: CashDealerTooltip,
    },
    {
      field: "executionFee", // NPV as Fee
      headerName: TableHeaderField.executionFee,
      colId: ColId.executionFee,
      width: defaultColumnWidths.executionFee,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellRenderer: ExecutionFeeCellRenderer,
    },

    {
      field: "dealtStrike",
      headerName: TableHeaderField.dealtStrike,
      colId: ColId.strike,
      width: defaultColumnWidths.strike,
      cellClass: "ag-roundedInputStyle ",
      cellStyle: { padding: "0px" },
      cellRendererParams: { fullTradeData: fullTradeData },
      cellRenderer: StrikeCellRenderer,
    },

    {
      field: "direction",
      headerName: TableHeaderField.direction,
      colId: ColId.direction,
      width: defaultColumnWidths.side,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellRenderer: DirectionCellRenderer,
      valueFormatter: directionValueFormatter,
    },
    {
      field: "coupon",
      headerName: TableHeaderField.coupon,
      colId: ColId.coupon,
      width: defaultColumnWidths.coupon,
      cellRenderer: CouponActionSummarySwapCellRenderer,
      valueFormatter: couponCellValueFormatter,
      cellRendererParams: { fullTradeData: fullTradeData },
    },
    {
      field: "notionalLeg1",
      headerName: TableHeaderField.notionalLeg1,
      colId: ColId.notionalLeg1,
      width: defaultColumnWidths.nominal,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellRenderer: NotionalLeg1,
      valueFormatter: notionalLeg1ValueFormatter,
    },
    {
      field: "quotedSize",
      headerName: TableHeaderField.quotedSize,
      colId: ColId.quotedSize,
      editable: (params: any) =>
      returnTrueIfCellCanBeEdited(params.data.statusER, ColId.quotedSize, undefined, fullTradeData.structureType as StructureTypeCode, fullTradeData, params),  
      cellClass: (params: any) => {
        return returnTrueIfCellCanBeEdited(params.data.statusER, ColId.quotedSize, undefined, fullTradeData.structureType as StructureTypeCode, fullTradeData, params) == true
          ? theme == "light"
            ? "inputCellOtherLight roundedInputCell"
            : "inputCellOtherDark roundedInputCell"
          : "roundedInputCell";
      },
      width: defaultColumnWidths.quotedSize,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellEditor: DynamicCellEditorQuoteSize,
      cellRenderer: quotedSizeLegRP,
      valueFormatter: (params: any) => {
        return params.data.quotedSize
          ? returnSizeFormatted(
              params.data.quotedSize,
              params.data.instrumentType,
            )
          : "";
      },
      cellDataType: "number",
    },
    {
      // % Quoted bar — hide: true so unselected paths default to hidden;
      // selection arrays opt in via ColId.percentQuoted when re-enabled.
      field: "quotedSize",
      headerName: TableHeaderField.percentQuoted,
      colId: ColId.percentQuoted,
      width: defaultColumnWidths.percentQuoted,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellRenderer: QuotedPercentCellRenderer,
      valueFormatter: quotedPercentValueFormatter,
      hide: true,
    },
    {
      field: "securityName",
      headerName: TableHeaderField.securityName,
      colId: ColId.security,
      width: defaultColumnWidths.name,
      // flex: 2,
      tooltipField: "isin",
    },
    // LQC-2153: FRN columns sit after Security - visibility controlled by tempColData/additionalColDefs
    {
      field: "referenceIndex",
      headerName: TableHeaderField.referenceIndex,
      colId: ColId.referenceIndex,
      width: defaultColumnWidths.referenceIndex,
      cellRenderer: ReferenceIndexCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
      valueFormatter: referenceIndexValueFormatter,
    },
    {
      field: "quotedMargin",
      headerName: TableHeaderField.quotedMargin,
      colId: ColId.quotedMargin,
      width: defaultColumnWidths.quotedMargin,
      cellRenderer: QuotedMarginCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
      valueFormatter: quotedMarginValueFormatter,
      cellDataType: "number",
    },
    {
      field: "coupon", // FRN Current Coupon reuses the block coupon
      headerName: TableHeaderField.currentCoupon,
      colId: ColId.currentCoupon,
      width: defaultColumnWidths.currentCoupon,
      cellRenderer: CurrentCouponCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
      valueFormatter: currentCouponValueFormatter,
      cellDataType: "number",
    },
    // LQC-227: ISIN column for bond trades — visibility controlled by selection arrays
    {
      field: "isin",
      headerName: "ISIN",
      colId: ColId.isin,
      width: 150,
    },
    {
      field: "leg",
      headerName: TableHeaderField.leg,
      colId: ColId.leg,
      width: defaultColumnWidths.leg,
    },
    {
      field: "extStructureId",
      headerName: TableHeaderField.extStructureId,
      colId: ColId.extStructureId,
      extraScaling: true,
      width: defaultColumnWidths.extStructureId,
    },
    {
      field: "settlementDate",
      headerName: TableHeaderField.settlementDate,
      colId: ColId.settlementDate,
      extraScaling: true,
      width: defaultColumnWidths.date,
      cellRenderer: SettlementDateCellRenderer,
      cellStyle: { padding: "0px" },
      cellClass: "ag-roundedInputStyle",
      cellRendererParams: { fullTradeData },
      valueFormatter: settlementDateValueFormatter,
      cellDataType: "dateString",
    },
    {
      field: "referencePrice", // Order
      headerName: TableHeaderField.level,
      colId: ColId.level,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellRenderer: LevelCellRenderer,
      valueFormatter: levelValueFormatter,
      width: defaultColumnWidths.level,
    },
    {
      field: "fillQuantity", // Order
      headerName: TableHeaderField.fillQuantity,
      colId: ColId.fillQuantity,
      width: defaultColumnWidths.fillQuantity,
      cellRenderer: FillsSummaryQuantityCellRenderer,
      tooltipValueGetter: displayPendingTooltipForFillQuantity,
      cellRendererParams: { fullTradeData: fullTradeData },
      valueFormatter: fillsSummaryQuantityValueFormatter,
    },
    {
      field: "fillPrice", // Order
      headerName: TableHeaderField.fillPrice, // Filled At
      colId: ColId.fillPrice,
      width: defaultColumnWidths.fillPrice,
      cellRenderer: FillsSummaryPriceCellRenderer,
      tooltipValueGetter: displayYieldTooltipForFills, // acts as the yield AND the displayPendingTooltipForFillQuantity
      cellRendererParams: { fullTradeData: fullTradeData },
      valueFormatter: fillsSummaryPriceValueFormatter,
    },
    {
      field: "filledPercent", // Order
      headerName: TableHeaderField.filledPercent,
      colId: ColId.filledPercent,
      width: defaultColumnWidths.filledPercent,
      cellRenderer: FillsSummaryPercentCellRenderer,
      tooltipValueGetter: displayPendingTooltipForFilledPercent,
      cellRendererParams: { fullTradeData: fullTradeData },
      valueFormatter: fillsSummaryPercentValueFormatter,
    },
    {
      field: "filledAt",
      headerName: TableHeaderField.filledAt, // Actioned At
      colId: ColId.filledAt,
      width: defaultColumnWidths.filledAt,
      cellStyle: { textAlign: "center" },
    },
    {
      field: "addFill", // Order - Block: Add (+), Fill: Remove (trash)
      colId: ColId.addFillButton,
      headerName: "",
      width: 20,
      extraScaling: true,
      resizable: false,
      suppressMovable: true,
      cellClass: "reducedPaddingLeft reducedPaddingRight",
      headerClass: "fixedWidthHeader",
      cellRenderer: AddFillButtonCellRenderer,
      cellRendererParams: { fullTradeData },
    },
    {
      field: "editFill", // Order - Block: empty, Fill: Edit (pencil)
      colId: ColId.editFillButton,
      headerName: "",
      width: 20,
      extraScaling: true,
      resizable: false,
      suppressMovable: true,
      cellClass: "reducedPaddingLeft reducedPaddingRight",
      headerClass: "fixedWidthHeader",
      cellRenderer: () => null, // Block rows show nothing; fill rows handled by wrapping
      cellRendererParams: { fullTradeData },
    },
    // {
    //   field: "referenceSpread", // Order
    //   headerName: TableHeaderField.refSpread,
    //   colId: ColId.refAsset,
    //   width: defaultColumnWidths.refAsset,
    // },
    // {
    //   field: "referenceAsset", // Order
    //   headerName: TableHeaderField.refAsset,
    //   colId: ColId.refAsset,
    //   width: defaultColumnWidths.refAsset,
    // },
    {
      field: "expiryDate",
      colId: ColId.expiryDate,
      headerName: TableHeaderField.expiryDate,
      width: defaultColumnWidths.date,
      cellRenderer: ExpiryDateCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
    },
    {
      field: "deliveryDate",
      colId: ColId.deliveryDate,
      headerName: TableHeaderField.deliveryDate,
      width: defaultColumnWidths.date,
      cellRenderer: (params: any) => {
        if (params.data) {
          if (params.value) {
            return returnFormattedDate(params.value);
          }
        } else return "";
      },
      cellDataType: "dateString",
    },
    {
      field: "indicativeMid", // never returned for RP
      headerName: TableHeaderField.indicativeMid,
      colId: ColId.indicativeMid,
      width: defaultColumnWidths.indicativeMid,
      cellRenderer: IndicativeMidCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
    },
    {
      field: "cpi", // Will be similar to Dealt Strike
      headerName: TableHeaderField.cpiLeg1,
      colId: ColId.cpiLeg1,
      width: defaultColumnWidths.cpi,
      editable: (params: any) =>
        isCPIEditable({
          esid: fullTradeData.esid,
          securityType: params.data.securityType,
        }),
      cellStyle: { padding: "0px" },
      cellClass: "ag-roundedInputStyle",
      cellRenderer: CPICellRenderer,
      cellEditor: CPICellEditor,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellDataType: "number",
    },
    {
      field: "cpiIndex",
      headerName: TableHeaderField.cpiIndex,
      colId: ColId.cpiIndex,
      width: defaultColumnWidths.cpiIndex,
      cellRendererParams: { fullTradeData: fullTradeData },
      cellRenderer: CpiIndexCellRenderer,
    },
    {
      field: "notionalLeg2",
      headerName: TableHeaderField.notionalLeg2,
      colId: ColId.notionalLeg2,
      editable: (params: any) =>
        returnTrueIfCellCanBeEdited(params.data.statusER),
      cellClass: (params: any) => {
        return returnTrueIfCellCanBeEdited(params.data.statusER) == true
          ? theme == "light"
            ? "inputCellOtherLight"
            : "inputCellOtherDark"
          : "";
      },
      cellDataType: "number",
      width: defaultColumnWidths.nominal,
      cellRenderer: (params: any) => {
        return params.value
          ? returnSizeFormatted(params.value, params.data.instrumentType)
          : "";
      },
    },
    {
      field: "book",
      headerName: TableHeaderField.book,
      colId: ColId.book,
      width: defaultColumnWidths.book,
      cellRenderer: BookCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData, theme: theme },
    },
    ...fillerColDef,
  ];
  const structureType = fullTradeData.structureType;
  const status = fullTradeData.status;

  const unwind = fullTradeData.unwind;
  let containsRecoupon = false;
  let containsNPV = false;
  if (fullTradeData && fullTradeData.legs && fullTradeData.legs.length > 0) {
    fullTradeData.legs.forEach((leg: any) => {
      if (leg.unwind == "Recoupon") {
        containsRecoupon = true;
      }
      if (leg.unwind == "NPV") {
        containsNPV = true;
      }
    });
  }
  let colDefsToReturn: ColDef | any = [];
  const columnsToShowForNPVorRecoupon: ColId[] = []; // USED FOR NPV / Recoupon to prevent cols later getting added.
  const columnsToHideForNPVorRecoupon: ColId[] = []; // USED FOR NPV / Recoupon to prevent cols later getting added.

  const dealtPriceCols: any[] = [];
  if (statusToShowDealtPrice.includes(status)) {
    dealtPriceCols.push(ColId.feeAdjustedQuote);
  }

  const bookCols: any[] = [];
  if (statusFromAwarded.includes(status)) {
    bookCols.push(ColId.book);
  }

  const type = fullTradeData.type;

  if (type == "RFQ") {
    if (!unwind) {
      if (containsNPV) {
        columnsToShowForNPVorRecoupon.push(ColId.unwind);
      } else if (containsRecoupon) {
        if (statusFromAwarded.includes(fullTradeData.status)) {
          columnsToShowForNPVorRecoupon.push(ColId.unwind);
        } else {
          if (!containsNPV) {
            // Don't hide the col if there is an NPV as it will need it.
            columnsToHideForNPVorRecoupon.push(ColId.unwind);
          }
        }
      } else {
        // If there is NO NPV and NO ReCoupon - This might happnen when a trade has had it's recoupon removed mid trade.
        columnsToHideForNPVorRecoupon.push(ColId.unwind);
      }
    }

    //NPV PACK
    // if (structureType == StructureTypeCode.NPVPACK) {
    if (unwind == true) {
      // HIDE NPV STYLE at all status
      // 14176 14191 14235 14242
      columnsToHideForNPVorRecoupon.push(
        ColId.bid,
        ColId.bidOutright,
        ColId.ask,
        ColId.askOutright,
        ColId.mid,
        ColId.midOutright,
        // Check with Jonty;
        ColId.feeAdjustedQuote,
      );

      if (
        statusFromReceivedToQuoted.includes(status) ||
        status == ExecutionRequestStatus.DealtAway
      ) {
        // 14176 SHOW NPV Style
        columnsToShowForNPVorRecoupon.push(ColId.coupon);
      } else if (status == ExecutionRequestStatus.Awarded) {
        // 14191 SHOW NPV Style
        columnsToShowForNPVorRecoupon.push(
          ColId.dealer,
          ColId.cash,
          ColId.coupon,
        );
      } else if (statusAtExecutedRange.includes(status)) {
        // 14235 SHOW NPV Style
        columnsToShowForNPVorRecoupon.push(
          ColId.dealer,
          ColId.cash,
          ColId.executionFee,
          ColId.coupon,
        );
      } else if (statusAtAffirmedRange.includes(status)) {
        // 14242 SHOW NPV Style
        columnsToShowForNPVorRecoupon.push(
          ColId.dealer,
          ColId.dealtFee,
          ColId.coupon,
        );
      }
    } else if (unwind == false && containsNPV) {
      // 14283
      if (!statusFormExecutedToAffirmedRange.includes(status)) {
        columnsToShowForNPVorRecoupon.push(
          ColId.coupon,
          ColId.dealer,
          ColId.cash,
        );
      } else if (statusAtExecutedRange.includes(status)) {
        // 14287
        columnsToShowForNPVorRecoupon.push(
          ColId.coupon,
          ColId.dealer,
          ColId.cash,
          ColId.executionFee,
          ColId.feeAdjustedQuote,
        );
      } else if (statusAtAffirmedRange.includes(status)) {
        // 14291
        columnsToShowForNPVorRecoupon.push(
          ColId.coupon,
          ColId.dealer,
          ColId.dealtFee,
        );
      }
    }

    if (unwind == false && containsRecoupon) {
      if (
        statusAtExecutedRange.includes(status) ||
        status == ExecutionStrategyStatus.Awarded // #14876, #14877, #14878, #14879, #14880, #14881 - Moving ReCoupon showing on Dealer and Cash cells forward to Awarded.
      ) {
        // 14251 SHOW
        columnsToShowForNPVorRecoupon.push(
          ColId.coupon,
          ColId.dealer,
          ColId.cash,
        );
        if (statusAtExecutedRange.includes(status)) {
          columnsToShowForNPVorRecoupon.push(ColId.executionFee);
          columnsToShowForNPVorRecoupon.push(ColId.feeAdjustedQuote);
        }
        // 14251 HIDE
        // columnsToHideForNPVorRecoupon.push(ColId.feeAdjustedQuote);
      } else if (statusAtAffirmedRange.includes(status)) {
        columnsToShowForNPVorRecoupon.push(
          ColId.bid,
          ColId.coupon,
          ColId.dealer,
          ColId.dealtFee,
        );
      }
    }

    // END OF NPV and Recoupon - Start back at IF.
    if (
      structureType == StructureTypeCode.SYNREPO ||
      structureType == StructureTypeCode.SYNREVREP
    ) {
      // ALLOW QUOTING AT THIS STATUS
      if (statusFromAwarded.includes(status)) {
        if (status == ExecutionStrategyStatus.Awarded) {
          const colDefsForAwardedRPLegs = [
            ColId.direction,
            ColId.notionalLeg1,
            ColId.security,
            ...(tradeHasBondOrBondOption(fullTradeData) ? [ColId.isin] : []),
            ColId.settlementDate,
            ColId.expiryDate,
            ColId.deliveryDate,
            ColId.bid,
            ColId.ask,
            ColId.mid,
            ColId.quotedSize,
            // LQC-275: % Quoted column disabled on RP leg until RP-side behaviour is finalised
            // ...(tradeHasReducedQuotedSize(fullTradeData) ? [ColId.percentQuoted] : []),
            ...bookCols,
            ...dealtPriceCols,
          ];
          colDefsToReturn = returnColDefsAsNewArray(
            legColDefsRP,
            colDefsForAwardedRPLegs,
            columnsToShowForNPVorRecoupon,
            columnsToHideForNPVorRecoupon,
          );
        } else if (statusAtExecutedRange.includes(status)) {
          const colDefsForExecutedGroupRPLegs = [
            ColId.direction,
            ColId.notionalLeg1,
            ColId.security,
            ...(tradeHasBondOrBondOption(fullTradeData) ? [ColId.isin] : []),
            ColId.settlementDate,
            ColId.expiryDate,
            ColId.deliveryDate,
            ColId.bid,
            ColId.ask,
            ColId.mid,
            ColId.strike,
            ColId.quotedSize,
            // LQC-275: % Quoted column disabled on RP leg until RP-side behaviour is finalised
            // ...(tradeHasReducedQuotedSize(fullTradeData) ? [ColId.percentQuoted] : []),
            ...bookCols,
            ...dealtPriceCols,
          ];
          colDefsToReturn = returnColDefsAsNewArray(
            legColDefsRP,
            colDefsForExecutedGroupRPLegs,
            columnsToShowForNPVorRecoupon,
            columnsToHideForNPVorRecoupon,
          );
        } else if (statusAtAffirmedRange.includes(status)) {
          const colDefsForAffirmedComletedGroupRPLegs = [
            ColId.direction,
            ColId.notionalLeg1,
            ColId.security,
            ...(tradeHasBondOrBondOption(fullTradeData) ? [ColId.isin] : []),
            ColId.settlementDate,
            ColId.expiryDate,
            ColId.deliveryDate,
            ColId.bid,
            ColId.ask,
            ColId.mid,
            ColId.strike,
            ColId.quotedSize,
            // LQC-275: % Quoted column disabled on RP leg until RP-side behaviour is finalised
            // ...(tradeHasReducedQuotedSize(fullTradeData) ? [ColId.percentQuoted] : []),
            ...bookCols,
            ...dealtPriceCols,
          ];
          colDefsToReturn = returnColDefsAsNewArray(
            legColDefsRP,
            colDefsForAffirmedComletedGroupRPLegs,
            columnsToShowForNPVorRecoupon,
            columnsToHideForNPVorRecoupon,
          );
        }
      } else {
        const colDefsForDefaultRPLegs = [
          ColId.direction,
          ColId.notionalLeg1,
          ColId.security,
          ...(tradeHasBondOrBondOption(fullTradeData) ? [ColId.isin] : []),
          ColId.settlementDate,
          ColId.expiryDate,
          ColId.deliveryDate,
          ColId.quotedSize,
          // LQC-275: % Quoted column disabled on RP leg until RP-side behaviour is finalised
          // ...(tradeHasReducedQuotedSize(fullTradeData) ? [ColId.percentQuoted] : []),
          ...bookCols,
          ...dealtPriceCols,
        ];
        colDefsToReturn = returnColDefsAsNewArray(
          legColDefsRP,
          colDefsForDefaultRPLegs,
          columnsToShowForNPVorRecoupon,
          columnsToHideForNPVorRecoupon,
        );
      }
    } else {
      const tempColData: ColId[] = [];
      if (
        fullTradeData &&
        fullTradeData.legs &&
        fullTradeData.legs.length > 0
      ) {
        fullTradeData.legs.forEach((leg: any) => {
          // edge cases of multiple securityTypes needing extra cols
          const securityType = leg.securityType;
          const instrumentType = leg.instrumentType;
          if (
            securityType == SecurityTypeCode.SWO ||
            securityType == SecurityTypeCode.BNDO
          ) {
            tempColData.push(ColId.strike);
            // gridApi.setColumnsVisible([ColId.dealtStrike], true);
          }
          if (securityType == SecurityTypeCode.ZCS) {
            tempColData.push(ColId.cpiLeg1, ColId.cpiIndex);
          }
          if (
            securityType == SecurityTypeCode.XCS ||
            securityType == SecurityTypeCode.XBS
          ) {
            tempColData.push(ColId.notionalLeg2);
          }
          if (instrumentType == InstrumentTypeCode.OPT) {
            tempColData.push(ColId.expiryDate);
          }
          if (instrumentType == InstrumentTypeCode.FRN) {
            tempColData.push(
              ColId.referenceIndex,
              ColId.quotedMargin,
              ColId.currentCoupon,
            );
          }
        });
      }

      const defaultColDefsRFQRPLegs = [
        ColId.direction,
        ColId.notionalLeg1,
        ColId.security,
        ...(tradeHasBondOrBondOption(fullTradeData) ? [ColId.isin] : []),
        ColId.settlementDate,
        ColId.bid,
        ColId.ask,
        ColId.mid,
        ColId.quotedSize,
        // LQC-275: % Quoted column disabled on RP leg until RP-side behaviour is finalised
        // ...(tradeHasReducedQuotedSize(fullTradeData) ? [ColId.percentQuoted] : []),
        ...bookCols,
        ...tempColData,
        ...dealtPriceCols,
      ];
      colDefsToReturn = returnColDefsAsNewArray(
        legColDefsRP,
        defaultColDefsRFQRPLegs,
        columnsToShowForNPVorRecoupon,
        columnsToHideForNPVorRecoupon,
      );
    }
  } else if (type == "Order") {
    // DEFAULT COL DEFS
    const defaultColDefsOrderRPLegs = [
      ColId.expand,
      ColId.direction,
      ColId.notionalLeg1,
      ColId.security,
      ...(tradeHasBondOrBondOption(fullTradeData) ? [ColId.isin] : []),
      ColId.settlementDate,
      ColId.level,
      ColId.fillQuantity,
      ColId.filledPercent,
      ColId.filledAt,
      ColId.fillPrice,
      ColId.addFillButton,
      ColId.editFillButton,
      ...dealtPriceCols,
      ...bookCols,
    ];

    // LQC-2153: FRN columns for Order legs (Orders only support BND/FUT, so FRN is a BND sub-type here)
    const tradeHasFRNLeg = fullTradeData.legs?.some(
      (leg: any) => leg.instrumentType == InstrumentTypeCode.FRN,
    );
    if (tradeHasFRNLeg) {
      defaultColDefsOrderRPLegs.push(
        ColId.referenceIndex,
        ColId.quotedMargin,
        ColId.currentCoupon,
      );
    }

    colDefsToReturn = returnColDefsAsNewArray(
      legColDefsRP,
      defaultColDefsOrderRPLegs,
      columnsToShowForNPVorRecoupon,
      columnsToHideForNPVorRecoupon,
      true, // order - Will remove the filler column.
    );

    // Reorder dealtPrice to appear after filledAt (Actioned At) for Orders
    colDefsToReturn = reorderColumnsForOrders(colDefsToReturn, [
      { colId: ColId.feeAdjustedQuote, afterColId: ColId.fillPrice },
    ]);

    // Wrap columns for flattened fills (block rows show summary, fill rows show individual)
    // addFillButton: Block shows Add (+), Fill shows Remove (trash)
    // editFillButton: Block shows nothing, Fill shows Edit (pencil)
    const dualColumnMapRP = new Map<
      string,
      {
        fillRenderer: any;
        fillFormatter?: any;
        fillEditable?: (params: any) => boolean;
        fillEditor?: any;
        fillCellClass?: string;
        fillValueGetter?: (params: any) => any;
      }
    >([
      [
        ColId.fillQuantity,
        {
          fillRenderer: FillQuantityCellRenderer,
          fillFormatter: fillQuantityValueFormatter,
          fillEditable: isFillQuantityCellEditable,
          fillEditor: FillQuantityCellEditor,
          fillCellClass: "ag-roundedInputStyle",
          fillValueGetter: (params: any) => params.data?.fillData?.fillSize,
        },
      ],
      [
        ColId.fillPrice,
        {
          fillRenderer: FillPriceCellRenderer,
          fillFormatter: fillPriceValueFormatter,
          fillEditable: isFillPriceCellEditable,
          fillEditor: FillPriceCellEditor,
          fillCellClass: "ag-roundedInputStyle",
          fillValueGetter: (params: any) => params.data?.fillData?.fillRate,
        },
      ],
      [
        ColId.filledPercent,
        {
          fillRenderer: FilledPercentCellRenderer,
          fillFormatter: filledPercentValueFormatter,
        },
      ],
      [
        ColId.filledAt,
        {
          fillRenderer: FilledAtCellRenderer,
          fillFormatter: filledAtValueFormatter,
        },
      ],
      [
        ColId.addFillButton,
        {
          fillRenderer: RemoveFillButtonCellRenderer,
        },
      ],
      [
        ColId.editFillButton,
        {
          fillRenderer: EditFillButtonCellRenderer,
        },
      ],
    ]);

    colDefsToReturn = wrapColDefsForOrderFills(
      colDefsToReturn,
      dualColumnMapRP,
      [], // No appended fill-only columns - Remove/Edit are now dual columns
      fullTradeData,
    );
  }
  return applyColumnWidthMultiplier(colDefsToReturn, columnWidthMultiplier);
}
